import React, { useState, useCallback, useEffect } from "react"
import { CircularProgress, AutocompleteProps, Box, Chip, Typography } from "@mui/material"
import {
  FilterCondition,
  FilterOperator,
  LogicalOperator,
  SortDirection,
  useGetProductsForOptionsLazyQuery
} from "src/graphql/quark/quark.type"
import InputAutocomplete from "src/shared/components/input-autocomplete"
import { NetworkStatus, WatchQueryFetchPolicy } from "@apollo/client"

export interface OptionSelectProduct {
  id: string
  name: string
  maximumRetailUnitPrice: number
  sellingUnit?: string | null
}

const SelectProduct: React.FC<
  {
    onChange: any
    error?: boolean
    selectedProducts?: OptionSelectProduct | OptionSelectProduct[] // Products to show in chips
    helperText?: string
    label?: string
    placeholder?: string
    required?: boolean
    startAdornment?: React.ReactNode
    fetchPolicy?: WatchQueryFetchPolicy
    multiple?: boolean
    filter?: FilterCondition[]
    onClose?: () => void // Callback when dropdown closes
  } & Omit<AutocompleteProps<any, any, any, any>, "renderInput" | "options" | "onChange">
> = ({
  onChange,
  error,
  helperText,
  selectedProducts = [],
  label,
  placeholder,
  required,
  startAdornment,
  fetchPolicy,
  multiple,
  filter,
  onClose,
  ...rest
}) => {
  const [options, setOptions] = useState<OptionSelectProduct[]>([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")

  const [getProducts, { error: getError, networkStatus }] = useGetProductsForOptionsLazyQuery({
    fetchPolicy: fetchPolicy || "network-only",
    notifyOnNetworkStatusChange: true
  })
  const refetching = networkStatus !== NetworkStatus.ready
  // Debounced search function
  const fetchOptions = useCallback(
    async (searchQuery: string) => {
      if (!searchQuery.trim()) {
        setOptions([])
        return
      }

      setLoading(true)
      try {
        const response = await getProducts({
          variables: {
            filter: {
              conditions: [
                {
                  operator: FilterOperator.Equal,
                  field: "status",
                  value: "ACTIVE"
                },
                {
                  operator: FilterOperator.Like,
                  field: "name",
                  value: searchQuery
                },
                ...(filter || [])
              ],
              logicalOperator: LogicalOperator.And
            },
            sort: [
              {
                direction: SortDirection.Asc,
                field: "name"
              }
            ],
            page: 1,
            limit: 500
          }
        })

        if (!response.data?.getProducts?.data?.length) {
          setOptions([])
          return
        }

        const resData: OptionSelectProduct[] = response.data.getProducts.data.map((val: any) => ({
          id: val.id as string,
          name: val.name as string,
          maximumRetailUnitPrice: val.maximumRetailUnitPrice as number,
          sellingUnit: val.sellingUnit ?? null
        }))
        setOptions(resData)
      } catch (error) {
        console.error("Error fetching products:", error)
        setOptions([])
      } finally {
        setLoading(false)
      }
    },
    [getProducts]
  )

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm) {
        fetchOptions(searchTerm)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [searchTerm, fetchOptions])

  // Handle input change for search
  const handleInputChange = useCallback((_: any, newInputValue: string) => {
    setSearchTerm(newInputValue)
  }, [])

  // Handle removing chips
  const handleRemoveChip = useCallback(
    (productToRemove: OptionSelectProduct) => {
      if (Array.isArray(selectedProducts)) {
        const newValue = selectedProducts.filter((item: OptionSelectProduct) => item.id !== productToRemove.id)
        onChange(newValue)
      } else {
        onChange(null)
      }
    },
    [selectedProducts, onChange]
  )

  if (!Array.isArray(selectedProducts) && multiple) {
    return <>on multiple true selectedProducts should be array</>
  }

  // Selected products for display are passed via props
  const selectedSingle = !multiple && !Array.isArray(selectedProducts) ? selectedProducts : undefined
  const selectedMultiple = multiple && Array.isArray(selectedProducts) ? selectedProducts : []

  const optionsWithSelected = (() => {
    if (multiple) {
      const missing = selectedMultiple.filter((selected) => !options.some((option) => option.id === selected.id))
      return missing.length ? [...missing, ...options] : options
    }
    if (selectedSingle?.id && !options.some((option) => option.id === selectedSingle.id)) {
      return [selectedSingle, ...options]
    }
    return options
  })()

  const autocompleteValue = multiple
    ? selectedMultiple
    : selectedSingle?.id
      ? (optionsWithSelected.find((option) => option.id === selectedSingle.id) ?? selectedSingle)
      : null

  return (
    <Box>
      <InputAutocomplete
        {...rest}
        label={label}
        options={optionsWithSelected}
        multiple={multiple}
        getOptionKey={(option) => option?.id ?? option}
        getOptionLabel={(option) => (typeof option === "string" ? option : (option?.name ?? ""))}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        onInputChange={handleInputChange}
        loading={loading || refetching}
        value={autocompleteValue}
        onChange={(val: any) => {
          //   if (multiple) {
          // For multiple selection, val is array of selected options
          if (Array.isArray(val) && multiple) {
            const newSelectedOptions = val || []
            const allSelected = [...newSelectedOptions, ...(selectedProducts as OptionSelectProduct[])]
            // Remove duplicates based on id
            const uniqueSelected = allSelected.filter(
              (item, index, self) => index === self.findIndex((t) => t.id === item.id)
            )
            onChange(uniqueSelected)
          } else {
            onChange(val)
          }
          //   } else {
          //     // For single selection
          //     onChange(val?.id || null)
          //     // Clear search and options after single selection
          //     setSearchTerm("")
          //     setOptions([])
          //   }
        }}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            {option.name}
          </li>
        )}
        showChip={"hidden"}
        placeholder={placeholder || "Type to search products..."}
        required={required}
        startAdornment={startAdornment}
        loadingText={<CircularProgress size={20} />}
        error={error ?? !!getError?.message}
        helperText={helperText ?? getError?.message}
        freeSolo={false}
        clearOnBlur={false}
        clearOnEscape={true}
        disableCloseOnSelect={multiple} // Keep dropdown open for multiple selection
        filterOptions={(x) => x} // Disable client-side filtering since we do server-side
      />

      {/* Display chips outside when showChip is "outside" */}
      {Array.isArray(selectedProducts) && selectedProducts.length > 0 && (
        <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 1, alignSelf: "center" }}>
            Selected:
          </Typography>
          {selectedProducts.map((product) => (
            <Chip
              key={product.id}
              label={product.name}
              onDelete={() => handleRemoveChip(product)}
              size="small"
              variant="outlined"
              color="primary"
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

export default SelectProduct
